import type { FiatPrice } from "@b2bportal/air-booking-api";
import type { Prices } from "@b2bportal/core-types";
import type { RewardsPrice } from "@hopper-b2b/types";
import { toSaneDecimalAmount } from "@overrides/utilities";

/**
 * Calculates the remaining fiat balance after rewards points are applied.
 *
 * toSaneDecimalAmount already rounds the result to 2 decimal places for CAD,
 * ensuring the card payment amount is valid currency without compounding rounding errors.
 *
 * @param tripTotal - The total trip price (from getTripTotalFromQuoteBreakdownWithProducts selector)
 * @param rewardsAmountToApply - The amount of rewards points to apply
 * @param rewardsAmountToApplyCashEquivalent - Cash value of the applied points, from the pointsToCash/quote brackets
 * @returns The remaining fiat balance, or undefined if trip total is not available
 */
export const getRemainingBalanceAfterRewardsApplied = (
  tripTotal: Prices | undefined,
  rewardsAmountToApply: RewardsPrice | undefined,
  rewardsAmountToApplyCashEquivalent: FiatPrice | undefined
): FiatPrice | undefined => {
  if (!tripTotal) return undefined;

  if (
    !rewardsAmountToApply?.value ||
    !rewardsAmountToApplyCashEquivalent ||
    rewardsAmountToApplyCashEquivalent.value <= 0
  ) {
    return tripTotal.fiat;
  }

  const fiatAfterRewards = toSaneDecimalAmount(
    tripTotal.fiat.value,
    rewardsAmountToApplyCashEquivalent.value,
    tripTotal.fiat.currencyCode
  );

  // If calculation failed, fall back to original fiat
  if (Number.isNaN(fiatAfterRewards)) {
    return tripTotal.fiat;
  }

  // Clamp to 0 to prevent negative values (including negative zero) from floating-point edge cases
  return {
    ...tripTotal.fiat,
    value: Math.max(0, fiatAfterRewards),
  };
};
